import { useState, useMemo } from 'react';

import { noop } from '../../../lib/noop';

import { HomeyUtils } from '../../../store/HomeyUtils';

import { useSelectedHomey } from '../../../store/SelectedHomeyStore.hooks';

import { EditNameTextField } from './EditNameTextField';
import { ZoneNavigationDraftItem } from './ZoneNavigationDraftItem';
import { ZoneNavigationItem } from './ZoneNavigationItem';
import { ZoneNavigationSortLine } from './ZoneNavigationSortLine';

export function ZoneNavigationList({
  tree,
  listToRender,
  byId,
  selectedId,
  draftZone,
  editId,
  icon,
  setDraftZone,
  setEditZoneId,
  onDrop,
  onSelect,
  onDraftZoneBlur,
  onEditBlur,
  onDraftZoneSave,
  onEditSave,
}) {
  const selectedHomey = useSelectedHomey();
  const [dragId, setDragId] = useState(null);
  const { root, descendants } = tree ?? {};

  const hasZoneSorting = HomeyUtils.getHasZoneSorting(selectedHomey);

  function handleItemPress(event) {
    onSelect?.({
      id: event.currentTarget.id,
    });
  }

  function handleDragBegin(dragItem) {
    setDragId(dragItem.id);
  }

  function handleDragEnd() {
    setDragId(null);
  }

  // Calculate possible drop zones between items
  const dropZones = useMemo(() => {
    if (listToRender == null) return [];

    const zones = [];
    // Add drop zone before each item
    listToRender.forEach((zone, index) => {
      const parents = [];
      // Walk up from current item to root, adding all parent levels as siblings
      let currentParentId = zone.parent;
      let currentLevel = zone.level;

      while (currentParentId) {
        parents.push({ id: currentParentId, level: currentLevel });

        const parentItem = byId[currentParentId];
        if (parentItem) {
          currentParentId = parentItem.parent;
          currentLevel--;
        } else {
          break;
        }
      }

      zones.push({
        index: index,
        parents: parents,
        beforeItemId: zone.id,
        parentsBefore: zones[index - 1]?.parents ?? [],
      });
    });

    // Add drop zone at the end (after last item)
    if (listToRender.length > 0) {
      zones.push({
        index: listToRender.length,
        parents: [],
        beforeItemId: null, // null means end of list
        parentsBefore: zones[zones.length - 1]?.parents ?? [],
      });
    }

    return zones;
  }, [listToRender, byId]);

  // Find drop zone at the end
  const dropZoneAtEnd = useMemo(
    () => dropZones.find((dz) => dz.beforeItemId === null),
    [dropZones],
  );

  if (listToRender == null) return null;

  return (
    <>
      {listToRender.map((item, index) => {
        const isValidDropTarget = dragId
          ? !descendants?.[dragId]?.has(item.id) && dragId !== item.id
          : true;
        const isParentDragging = dragId ? descendants?.[dragId]?.has(item.id) : false;
        const isSelected = item.id === selectedId || (selectedId == null && root.id === item.id);
        const isEdit = item.id === editId;

        // Find drop zone before this item
        const dropZoneBefore = dropZones.find((dz) => dz.beforeItemId === item.id);

        return (
          <div key={item.id}>
            {hasZoneSorting && dropZoneBefore && dragId && (
              <ZoneNavigationSortLine
                dropZone={dropZoneBefore}
                dragId={dragId}
                descendants={descendants}
                onDrop={onDrop ?? noop}
              />
            )}

            {draftZone && item.id === draftZone.id ? (
              <ZoneNavigationDraftItem
                item={item}
                icon={icon}
                draftZone={draftZone}
                onDraftZoneBlur={onDraftZoneBlur}
                onDraftZoneSave={onDraftZoneSave}
                EditNameTextField={EditNameTextField}
              />
            ) : (
              <ZoneNavigationItem
                item={item}
                byId={byId}
                icon={icon}
                isSelected={isSelected}
                isEdit={isEdit}
                isValidDropTarget={isValidDropTarget}
                isParentDragging={isParentDragging}
                setDraftZone={setDraftZone}
                setEditZoneId={setEditZoneId}
                onDragBegin={handleDragBegin}
                onDragEnd={handleDragEnd}
                onDrop={onDrop}
                onClick={handleItemPress}
                onEditBlur={onEditBlur}
                onEditSave={onEditSave}
                EditNameTextField={EditNameTextField}
              />
            )}
          </div>
        );
      })}
      {hasZoneSorting && dropZoneAtEnd && dragId && (
        <ZoneNavigationSortLine
          dropZone={dropZoneAtEnd}
          dragId={dragId}
          descendants={descendants}
          onDrop={onDrop ?? noop}
        />
      )}
    </>
  );
}
